import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Star, MapPin, Clock, ShoppingBag, Plus, X, Package, CheckCircle2 } from "lucide-react";

const STATUS_LABELS = {
  pending: "Pending",
  accepted: "Accepted",
  picked_up: "Picked Up",
  washing: "Washing",
  ready: "Ready",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  accepted: "bg-blue-100 text-blue-800",
  picked_up: "bg-indigo-100 text-indigo-800",
  washing: "bg-purple-100 text-purple-800",
  ready: "bg-emerald-100 text-emerald-800",
  delivered: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

export default function CustomerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [orderForm, setOrderForm] = useState({
    items: [{ service: "Wash & Fold", quantity: 3, unit_price: 80 }],
    pickup_address: "",
    pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user || user.role !== "customer") {
      navigate("/login");
      return;
    }
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/orders").then((r) => setOrders(r.data));
  }, [user, loading, navigate]);

  const refreshOrders = () => api.get("/orders").then((r) => setOrders(r.data));

  const openOrderModal = (partner) => {
    setError("");
    setSelectedPartner(partner);
    setOrderForm({
      items: [{ service: partner.services[0] || "Wash & Fold", quantity: 3, unit_price: partner.price_per_kg }],
      pickup_address: "",
      pickup_date: new Date(Date.now() + 86400000).toISOString().slice(0, 16),
      notes: "",
    });
  };

  const addItem = () =>
    setOrderForm((f) => ({ ...f, items: [...f.items, { service: selectedPartner.services[0], quantity: 1, unit_price: selectedPartner.price_per_kg }] }));
  const removeItem = (i) =>
    setOrderForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updateItem = (i, k, v) =>
    setOrderForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)) }));

  const total = orderForm.items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0);

  const submitOrder = async () => {
    setError("");
    if (!orderForm.pickup_address.trim()) return setError("Pickup address is required");
    setSubmitting(true);
    try {
      await api.post("/orders", { partner_id: selectedPartner.id, ...orderForm });
      setSelectedPartner(null);
      setTab("orders");
      refreshOrders();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelOrder = async (id) => {
    await api.put(`/orders/${id}/status`, { status: "cancelled" });
    refreshOrders();
  };

  if (loading || !user || user.role !== "customer") return null;

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="mb-8">
          <div className="label-eyebrow mb-2">Customer Dashboard</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Hi, {user.name} 👋</h1>
          <p className="text-muted-foreground mt-2">Find a trusted laundry near you and book in seconds.</p>
        </div>

        <div className="flex gap-2 mb-8 border-b border-border">
          {[{ k: "browse", l: "Browse Laundries" }, { k: "orders", l: `My Orders (${orders.length})` }].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 font-semibold text-sm border-b-2 transition-colors ${tab === t.k ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`customer-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "browse" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="partners-grid">
            {partners.map((p) => (
              <div key={p.id} className="bg-white rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow" data-testid={`partner-card-${p.id}`}>
                <div className="h-44 overflow-hidden">
                  <img src={p.image_url} alt={p.shop_name} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-display font-bold text-xl">{p.shop_name}</h3>
                    <div className="flex items-center gap-1 text-[#C2410C] flex-shrink-0">
                      <Star className="w-4 h-4 fill-[#C2410C]" />
                      <span className="font-bold text-sm">{p.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {p.locality}, {p.city}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{p.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {p.services.slice(0, 3).map((s) => (
                      <span key={s} className="text-xs bg-secondary px-2 py-1 rounded-md font-medium">{s}</span>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-muted-foreground">From</div>
                      <div className="font-bold text-lg">₹{p.price_per_kg}<span className="text-sm text-muted-foreground">/kg</span></div>
                    </div>
                    <button
                      onClick={() => openOrderModal(p)}
                      className="px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm tactile-btn"
                      data-testid={`book-partner-${p.id}`}
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="orders-list">
            {orders.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-border">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <div className="font-semibold mb-1">No orders yet</div>
                <div className="text-sm text-muted-foreground">Browse laundries to place your first order!</div>
              </div>
            )}
            {orders.map((o) => (
              <div key={o.id} className="bg-white rounded-xl border border-border p-5" data-testid={`order-card-${o.id}`}>
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <div className="font-bold text-lg">{o.partner_shop_name}</div>
                    <div className="text-xs text-muted-foreground">Order #{o.id.slice(0, 8)} • {new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</span>
                </div>
                <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {o.pickup_address}</div>
                <div className="text-sm text-muted-foreground mb-3 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Pickup: {new Date(o.pickup_date).toLocaleString()}</div>
                <div className="space-y-1 text-sm border-t border-border pt-3 mb-3">
                  {o.items.map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span>{it.service} × {it.quantity}</span>
                      <span>₹{(it.quantity * it.unit_price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between border-t border-border pt-3">
                  <div className="font-bold text-lg">Total: ₹{o.total_amount.toFixed(2)}</div>
                  {(o.status === "pending" || o.status === "accepted") && (
                    <button
                      onClick={() => cancelOrder(o.id)}
                      className="text-sm text-destructive font-semibold hover:underline"
                      data-testid={`cancel-order-${o.id}`}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order Modal */}
      {selectedPartner && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-4 py-8" onClick={() => setSelectedPartner(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-modal">
            <div className="sticky top-0 bg-white border-b border-border p-5 flex items-center justify-between">
              <div>
                <div className="label-eyebrow mb-1">Book with</div>
                <h2 className="font-display font-bold text-2xl">{selectedPartner.shop_name}</h2>
              </div>
              <button onClick={() => setSelectedPartner(null)} className="p-2 hover:bg-secondary rounded-lg" data-testid="close-order-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {error && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="order-error">{error}</div>}

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold">Items</label>
                  <button onClick={addItem} className="text-sm text-[#1E3A8A] font-semibold flex items-center gap-1" data-testid="add-item-button">
                    <Plus className="w-4 h-4" /> Add item
                  </button>
                </div>
                <div className="space-y-2">
                  {orderForm.items.map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <select
                        value={it.service}
                        onChange={(e) => updateItem(i, "service", e.target.value)}
                        className="col-span-5 px-3 py-2 rounded-lg border border-border text-sm"
                        data-testid={`item-service-${i}`}
                      >
                        {selectedPartner.services.map((s) => <option key={s}>{s}</option>)}
                      </select>
                      <input
                        type="number" min="0.5" step="0.5"
                        value={it.quantity}
                        onChange={(e) => updateItem(i, "quantity", parseFloat(e.target.value) || 0)}
                        className="col-span-3 px-3 py-2 rounded-lg border border-border text-sm"
                        placeholder="kg"
                        data-testid={`item-quantity-${i}`}
                      />
                      <input
                        type="number" min="0"
                        value={it.unit_price}
                        onChange={(e) => updateItem(i, "unit_price", parseFloat(e.target.value) || 0)}
                        className="col-span-3 px-3 py-2 rounded-lg border border-border text-sm"
                        placeholder="₹/kg"
                        data-testid={`item-price-${i}`}
                      />
                      {orderForm.items.length > 1 && (
                        <button onClick={() => removeItem(i)} className="col-span-1 text-destructive p-2" data-testid={`remove-item-${i}`}>
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold mb-1.5 block">Pickup address</label>
                <textarea
                  value={orderForm.pickup_address}
                  onChange={(e) => setOrderForm((f) => ({ ...f, pickup_address: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  placeholder="Flat 401, Building A, Koramangala..."
                  data-testid="pickup-address-input"
                />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1.5 block">Pickup date & time</label>
                <input
                  type="datetime-local"
                  value={orderForm.pickup_date}
                  onChange={(e) => setOrderForm((f) => ({ ...f, pickup_date: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  data-testid="pickup-date-input"
                />
              </div>
              <div>
                <label className="text-sm font-semibold mb-1.5 block">Notes (optional)</label>
                <input
                  value={orderForm.notes}
                  onChange={(e) => setOrderForm((f) => ({ ...f, notes: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                  placeholder="Any special instructions..."
                  data-testid="order-notes-input"
                />
              </div>

              <div className="bg-secondary rounded-xl p-4 flex items-center justify-between">
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total</div>
                  <div className="font-display font-black text-3xl">₹{total.toFixed(2)}</div>
                </div>
                <div className="text-xs text-muted-foreground">Cash on Delivery</div>
              </div>

              <button
                onClick={submitOrder}
                disabled={submitting}
                className="w-full py-3.5 rounded-lg bg-[#1E3A8A] text-white font-bold tactile-btn flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="confirm-order-button"
              >
                <CheckCircle2 className="w-5 h-5" /> {submitting ? "Placing order..." : "Confirm Order"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
